/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

/** The pad's own size; the page scales it to fit, and strokes are scaled back. */
const PAD_WIDTH = 640;
const PAD_HEIGHT = 200;
/** Clear space kept round the strokes when the picture is cropped to them. */
const MARGIN = 8;
/** Dark blue-black, as a ballpoint pen signs. */
const INK = '#1e2a5a';

/**
 * A signature field: drawn with a mouse, finger or pen, or typed as a name
 * for anyone who cannot draw one. Each stroke sends the picture, cropped to
 * the ink, as a PNG data: URL; the jobs service checks it again and prints it
 * as a file beside the page.
 */
@Component({
  selector: 'app-form-signature',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="aca-frmsig" [class.aca-frmsig--bad]="invalid" role="group" [attr.aria-labelledby]="labelId">
      <div class="aca-frmsig__modes">
        <button
          type="button"
          class="aca-frmsig__mode"
          [class.aca-frmsig__mode--on]="mode === 'draw'"
          [attr.aria-pressed]="mode === 'draw'"
          (click)="use('draw')"
        >
          <mat-icon>draw</mat-icon>{{ 'DMS.FORMS.SIGNATURE_DRAW' | translate }}
        </button>
        <button
          type="button"
          class="aca-frmsig__mode"
          [class.aca-frmsig__mode--on]="mode === 'type'"
          [attr.aria-pressed]="mode === 'type'"
          (click)="use('type')"
        >
          <mat-icon>keyboard</mat-icon>{{ 'DMS.FORMS.SIGNATURE_TYPE' | translate }}
        </button>
      </div>

      <ng-container *ngIf="mode === 'draw'">
        <!-- signed before this pad was shown (the field was hidden and shown again) -->
        <div class="aca-frmsig__box" *ngIf="value && !ink; else pad">
          <img class="aca-frmsig__picture" [src]="value" [alt]="'DMS.FORMS.SIGNATURE_DONE' | translate" />
        </div>
        <ng-template #pad>
          <div class="aca-frmsig__box">
            <canvas
              #canvas
              class="aca-frmsig__pad"
              [attr.width]="padWidth"
              [attr.height]="padHeight"
              role="img"
              [attr.aria-label]="'DMS.FORMS.SIGNATURE_PAD_LABEL' | translate"
              (pointerdown)="start($event)"
              (pointermove)="move($event)"
              (pointerup)="end()"
              (pointercancel)="end()"
            ></canvas>
            <span class="aca-frmsig__line" aria-hidden="true"></span>
            <span class="aca-frmsig__hint" *ngIf="!ink" aria-hidden="true">{{ 'DMS.FORMS.SIGNATURE_HINT' | translate }}</span>
          </div>
        </ng-template>
      </ng-container>

      <ng-container *ngIf="mode === 'type'">
        <input
          class="aca-frmsig__typed"
          [id]="inputId"
          [attr.aria-labelledby]="labelId"
          [placeholder]="'DMS.FORMS.SIGNATURE_TYPED_PLACEHOLDER' | translate"
          maxlength="60"
          autocomplete="name"
          [(ngModel)]="typed"
          (ngModelChange)="typeName()"
        />
        <div class="aca-frmsig__box aca-frmsig__box--typed" *ngIf="value">
          <img class="aca-frmsig__picture" [src]="value" [alt]="typed" />
        </div>
      </ng-container>

      <button type="button" class="aca-frmsig__clear" *ngIf="value || ink" (click)="clear()">
        <mat-icon>restart_alt</mat-icon>{{ 'DMS.FORMS.SIGNATURE_AGAIN' | translate }}
      </button>
    </div>
  `
})
export class FormSignatureComponent {
  /** The PNG data: URL signed so far, or empty. */
  @Input() value = '';
  @Input() invalid = false;
  /** The field's label, which names the pad. */
  @Input() labelId = '';
  @Input() inputId = '';
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('canvas') canvas?: ElementRef<HTMLCanvasElement>;

  readonly padWidth = PAD_WIDTH;
  readonly padHeight = PAD_HEIGHT;
  mode: 'draw' | 'type' = 'draw';
  typed = '';
  /** Something has been drawn on this pad. */
  ink = false;
  private drawing = false;

  use(mode: 'draw' | 'type'): void {
    if (mode !== this.mode) {
      this.mode = mode;
      this.clear();
    }
  }

  start(event: PointerEvent): void {
    const ctx = this.context();
    if (!ctx || (event.pointerType === 'mouse' && event.button !== 0)) {
      return;
    }
    event.preventDefault();
    this.drawing = true;
    this.ink = true;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    const [x, y] = this.point(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // a tap is a dot
    ctx.lineTo(x + 0.1, y);
    ctx.stroke();
  }

  move(event: PointerEvent): void {
    const ctx = this.context();
    if (!this.drawing || !ctx) {
      return;
    }
    // a fast pen sends several points per event; all of them make a smoother line
    const points = event.getCoalescedEvents?.() ?? [];
    for (const e of points.length ? points : [event]) {
      const [x, y] = this.point(e);
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  end(): void {
    if (this.drawing) {
      this.drawing = false;
      const canvas = this.canvas?.nativeElement;
      this.valueChange.emit(canvas ? cropped(canvas) : '');
    }
  }

  /** The typed name in a hand-written style, as the picture sent. */
  typeName(): void {
    const name = this.typed.trim();
    if (!name) {
      this.valueChange.emit('');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = PAD_WIDTH;
    canvas.height = PAD_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    const family = getComputedStyle(document.body).fontFamily || 'sans-serif';
    let size = 64;
    do {
      ctx.font = `italic 500 ${size}px ${family}`;
      size -= 4;
    } while (size > 20 && ctx.measureText(name).width > PAD_WIDTH - 2 * MARGIN);
    ctx.fillStyle = INK;
    ctx.textBaseline = 'middle';
    ctx.fillText(name, MARGIN, PAD_HEIGHT / 2);
    this.valueChange.emit(cropped(canvas));
  }

  clear(): void {
    const canvas = this.canvas?.nativeElement;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    this.ink = false;
    this.drawing = false;
    this.typed = '';
    this.valueChange.emit('');
  }

  private context(): CanvasRenderingContext2D | null {
    const ctx = this.canvas?.nativeElement.getContext('2d') ?? null;
    if (ctx) {
      ctx.lineWidth = 3.2;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.strokeStyle = INK;
    }
    return ctx;
  }

  /** Where the pointer is on the pad's own grid: the page shows the pad at another size. */
  private point(event: PointerEvent): [number, number] {
    const rect = (this.canvas?.nativeElement ?? (event.target as HTMLElement)).getBoundingClientRect();
    return [((event.clientX - rect.left) / (rect.width || 1)) * PAD_WIDTH, ((event.clientY - rect.top) / (rect.height || 1)) * PAD_HEIGHT];
  }
}

/** The picture cut down to the ink and a little room round it, so a signature prints at its own size. */
function cropped(canvas: HTMLCanvasElement): string {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }
  const { width, height } = canvas;
  const pixels = ctx.getImageData(0, 0, width, height).data;
  let left = width;
  let right = -1;
  let top = height;
  let bottom = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pixels[(y * width + x) * 4 + 3] > 0) {
        left = Math.min(left, x);
        right = Math.max(right, x);
        top = Math.min(top, y);
        bottom = Math.max(bottom, y);
      }
    }
  }
  if (right < 0) {
    return '';
  }
  left = Math.max(0, left - MARGIN);
  top = Math.max(0, top - MARGIN);
  right = Math.min(width - 1, right + MARGIN);
  bottom = Math.min(height - 1, bottom + MARGIN);
  const out = document.createElement('canvas');
  out.width = right - left + 1;
  out.height = bottom - top + 1;
  out.getContext('2d')?.drawImage(canvas, left, top, out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}
